interface VideoSource {
	width: number;
	height: number;
	source: string;
}

export class MrVideo extends HTMLElement {
	connectedCallback() {
		requestAnimationFrame( () => {
			if ( this.closest( 'mr-video-overlay' ) ) {
				return;
			}

			this.setupVideoPlayback();
		} );
	}

	disconnectedCallback() {
		const video = this.getAttachedVideo();
		if ( !video ) {
			return;
		}
	}

	async play() {
		const video = this.getAttachedVideo();
		if ( !video ) {
			return;
		}

		if ( !video.paused ) {
			return;
		}

		try {
			video.addEventListener( 'timeupdate', () => {
				video.setAttribute( 'data-playback-started', '' );
			}, {
				once: true,
			} );

			await video.play();
		} catch ( err ) {
			console.warn( err );
			// Do nothing.
		}
	}

	async pause() {
		const video = this.getAttachedVideo();
		if ( !video ) {
			return;
		}

		video.pause();
	}

	parseVideoSources( source: string | null ): Array<VideoSource> | null {
		if ( !source ) {
			return null;
		}

		const videos = JSON.parse( source ) as Array<VideoSource> | null;
		if ( !videos || !videos.length ) {
			return null;
		}

		return videos;
	}

	videoSources(): Array<VideoSource> | null {
		let videoSources = this.parseVideoSources( this.getAttribute( 'video-sources' ) );
		const videoSourcesMobile = this.parseVideoSources( this.getAttribute( 'video-sources-mobile' ) );
		const videoSourcesDesktop = this.parseVideoSources( this.getAttribute( 'video-sources-desktop' ) );
		if ( videoSourcesMobile && window.matchMedia( '(max-width: 63.999rem)' ).matches ) {
			videoSources = videoSourcesMobile;
		} else if ( videoSourcesDesktop ) {
			videoSources = videoSourcesDesktop;
		}

		if ( !videoSources ) {
			return null;
		}

		return videoSources;
	}

	setupVideoPlayback() {
		const video = this.getAttachedVideo();
		if ( !video ) {
			return;
		}

		const videos = this.videoSources();
		if ( !videos || !videos.length ) {
			return;
		}

		videos.sort( ( a, b ) => {
			return a.width - b.width;
		} );

		const width = this.getWidth();
		const height = this.getHeight();

		let selectedVideo = videos[0].source;
		videos.forEach( ( videoSource ) => {
			if ( videoSource.width <= width || videoSource.height <= height ) {
				selectedVideo = videoSource.source;
			}
		} );

		if ( video.src === selectedVideo ) {
			return;
		}

		video.src = selectedVideo;
	}

	getWidth() {
		let multiplier = 1;
		if ( ( 'devicePixelRatio' in window ) && window.devicePixelRatio ) {
			multiplier = Math.max( 1, Math.min( window.devicePixelRatio, 1.5 ) );
		}

		const rect = this.parentElement?.getBoundingClientRect();
		if ( !rect || !rect.width ) {
			return window.innerWidth * multiplier;
		}

		return rect.width * multiplier;
	}

	getHeight() {
		let multiplier = 1;
		if ( ( 'devicePixelRatio' in window ) && window.devicePixelRatio ) {
			multiplier = Math.max( 1, Math.min( window.devicePixelRatio, 1.5 ) );
		}

		const rect = this.parentElement?.getBoundingClientRect();
		if ( !rect || !rect.height ) {
			return window.innerHeight * multiplier;
		}

		return rect.height * multiplier;
	}

	getAttachedVideo(): HTMLVideoElement| null {
		const videoId = this.getAttribute( 'for' );
		if ( !videoId ) {
			return null;
		}

		const video = document.getElementById( videoId );
		if ( !video || !( video instanceof HTMLVideoElement ) ) {
			return null;
		}

		return video;
	}
}

customElements.define( 'mr-video', MrVideo );
